// src/firebase/registrations.ts
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  writeBatch,
  runTransaction,
  serverTimestamp,
  Timestamp,
  QueryDocumentSnapshot,
  DocumentData,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "./config";
import { withCache, invalidateCachePrefix } from "../utils/cache";
import { invalidateAuditLogCache } from "./auditLog";

// ── Cache TTLs ────────────────────────────────────────────────────────────────
// Registrations change often (every coach action + admin edits), so TTLs are
// short. Every write in this file calls invalidateRegistrationsCache(),
// which bulk-clears the entire `registrations:` namespace.
const TTL_REG_LIST   = 60 * 1000;       // 1 min — admin-wide lists
const TTL_REG_SCOPED = 2 * 60 * 1000;   // 2 min — per-coach / per-comp / per-pkg

function invalidateRegistrationsCache(): void {
  // Coarse but safe — any registration write may affect any cached view.
  invalidateCachePrefix("registrations:");
}

// Prepaid registrations mutate a credit-package doc inside the same
// transaction, so we also bust the creditPackages namespace to keep the
// per-coach "remaining credits" reads in sync. The bust here happens on
// the client that made the change; other clients refresh on their own TTL.
function invalidateCreditPackagesCacheFromRegistration(): void {
  invalidateCachePrefix("creditPackages:");
}

export interface Registration {
  id?: string;
  competitionId: string;
  competitionName: string;
  athleteId: string;
  athleteSnapshot: {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
  };
  club: string;
  weightCategory: string;
  division: "Male" | "Female";
  category: string;
  price: number;
  paymentId: string;
  referenceCode: string;
  coachId: string;
  coachEmail?: string;
  /**
   * Denormalized snapshot of the coach's display name at registration time.
   * Mirrors the athleteSnapshot pattern so the PUBLIC registrations page can
   * show who registered each athlete without reading the (private) users
   * collection. Optional: older docs and server-written online/BOG docs may
   * lack it, in which case the UI falls back to a name derived from coachEmail.
   */
  coachName?: string;
  /**
   * Set whenever `weightCategory` is changed via updateRegistration (coach or
   * admin). Drives the rate-limit gate and the public "weight updated by …"
   * line. Absent on registrations whose weight has never been edited.
   */
  weightLastChanged?: Timestamp;
  /** Denormalized display name (or email) of whoever last changed the weight. */
  weightChangedBy?: string;
  /**
   * How many times a COACH has changed this registration's weight. Capped at
   * MAX_WEIGHT_CHANGES by both the app and the Firestore rules. Admin edits do
   * NOT increment it (admins are exempt). Absent on docs whose weight a coach
   * has never changed — treated as 0 everywhere.
   */
  weightChangeCount?: number;
  registeredAt: Timestamp;
  isFree?: boolean;
  /** Added in Phase 15. Older docs without this field are treated as "online". */
  paymentType?: "online" | "prepaid" | "admin_manual";
  /** Set when paymentType === "prepaid" — links to the creditPackages document. */
  creditPackageId?: string;
}

// ---------------------------------------------------------------------------
// Deadline helpers
// ---------------------------------------------------------------------------

/**
 * Returns the cutoff Date after which coaches can no longer edit a
 * registration's weight/division for the given competition.
 *
 * Prefers `competition.weightChangeDeadline` (the federation can set a
 * dedicated cutoff in the admin form); falls back to `competition.startDate`
 * so legacy competitions without the new field still behave sensibly —
 * coaches lose edit rights when the competition begins.
 *
 * Returns `null` if neither date is present (treat as locked).
 */
export function getWeightChangeDeadline(comp: any): Date | null {
  const ts = comp?.weightChangeDeadline ?? comp?.startDate;
  if (!ts) return null;
  if (typeof ts.toDate === "function") return ts.toDate();
  return new Date(ts);
}

/** True when the coach owns the registration and the deadline has not passed. */
export function canCoachEditRegistration(
  reg: Pick<Registration, "coachId">,
  comp: any,
  coachId: string
): boolean {
  if (!coachId || reg.coachId !== coachId) return false;
  const deadline = getWeightChangeDeadline(comp);
  if (!deadline) return false;
  return deadline.getTime() > Date.now();
}

/** One hour, in milliseconds — the minimum gap between coach weight edits. */
export const WEIGHT_CHANGE_COOLDOWN_MS = 60 * 60 * 1000;

/**
 * Client-side rate limit for coach weight edits: a coach may change a given
 * athlete's weight at most once per hour. Returns true when a change is
 * currently allowed. Admins bypass this entirely (the caller skips the check).
 *
 * Accepts a Firestore Timestamp, a Date, or anything Date-parseable in
 * `weightLastChanged` so it works on both freshly-read docs and optimistic
 * local state.
 */
export function canChangeWeight(
  reg: { weightLastChanged?: any },
  currentTime: Date = new Date()
): boolean {
  const last = reg.weightLastChanged;
  if (!last) return true;
  const lastDate: Date =
    last && typeof last.toDate === "function" ? last.toDate() : new Date(last);
  if (isNaN(lastDate.getTime())) return true;
  return currentTime.getTime() - lastDate.getTime() >= WEIGHT_CHANGE_COOLDOWN_MS;
}

/**
 * Hard cap on how many times a coach may change a single athlete's weight,
 * independent of the once-per-hour cooldown. Keep this in sync with the
 * matching literal in firestore.rules (the rules can't import this constant).
 */
export const MAX_WEIGHT_CHANGES = 5;

/** Coach-made weight changes this registration has used so far (0+). */
export function weightChangesUsed(reg: { weightChangeCount?: number }): number {
  return reg.weightChangeCount ?? 0;
}

/** Coach-made weight changes still available before the hard cap. */
export function weightChangesRemaining(reg: { weightChangeCount?: number }): number {
  return Math.max(0, MAX_WEIGHT_CHANGES - weightChangesUsed(reg));
}

/** True while the coach still has at least one weight change left. */
export function canChangeWeightCount(reg: { weightChangeCount?: number }): boolean {
  return weightChangesUsed(reg) < MAX_WEIGHT_CHANGES;
}

// ---------------------------------------------------------------------------
// Grouped stats types
// ---------------------------------------------------------------------------

export interface ClubStats {
  clubName: string;
  athletes: Registration[];
  totalAmount: number;
}

export interface CompetitionStats {
  competitionId: string;
  competitionName: string;
  clubs: ClubStats[];
  totalAthletes: number;
  totalClubs: number;
  totalRevenue: number;
}

// ---------------------------------------------------------------------------
// Reference code helpers
// ---------------------------------------------------------------------------

const getNextManualRefCode = async (): Promise<string> => {
  const year = new Date().getFullYear();
  const counterRef = doc(db, "counters", `manualReg-${year}`);

  const newCount = await runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef);
    const current = snap.exists() ? (snap.data().count as number) : 0;
    const next = current + 1;
    tx.set(counterRef, { count: next, year });
    return next;
  });

  return `BANK-${year}-${String(newCount).padStart(3, "0")}`;
};

// ---------------------------------------------------------------------------
// WRITE
// ---------------------------------------------------------------------------

/**
 * Creates standard online (mock-payment) registration documents.
 * paymentType defaults to "online" if not provided.
 */
export const createRegistrationBatch = async (
  registrations: Omit<Registration, "id" | "registeredAt">[],
  paymentType: "online" | "prepaid" | "admin_manual" = "online"
): Promise<void> => {
  const batch = writeBatch(db);
  const now = Timestamp.now();

  registrations.forEach((reg) => {
    const docRef = doc(collection(db, "registrations"));
    batch.set(docRef, { ...reg, registeredAt: now, paymentType });
  });

  await batch.commit();
  invalidateRegistrationsCache();
};

/**
 * Creates FREE (admin manual / bank-transfer) registration documents.
 * Tagged with isFree: true and paymentType: "admin_manual".
 * Returns shared reference code (BANK-YYYY-NNN).
 */
export const createFreeRegistrationBatch = async (
  coachId: string,
  competitionId: string,
  competitionName: string,
  athletes: Array<{
    athleteId: string;
    athleteSnapshot: {
      firstName: string;
      lastName: string;
      dateOfBirth: string;
    };
    club: string;
    weightCategory: string;
    division: "Male" | "Female";
    category: string;
    price: number;
  }>,
  coachEmail?: string,
  coachName?: string
): Promise<string> => {
  const referenceCode = await getNextManualRefCode();
  const batch = writeBatch(db);
  const now = Timestamp.now();

  athletes.forEach((athlete) => {
    const docRef = doc(collection(db, "registrations"));
    batch.set(docRef, {
      competitionId,
      competitionName,
      athleteId: athlete.athleteId,
      athleteSnapshot: athlete.athleteSnapshot,
      club: athlete.club,
      weightCategory: athlete.weightCategory,
      division: athlete.division,
      category: athlete.category,
      price: athlete.price,
      paymentId: "free",
      referenceCode,
      coachId,
      coachEmail: coachEmail ?? "",
      coachName: coachName ?? "",
      registeredAt: now,
      isFree: true,
      paymentType: "admin_manual",
    });
  });

  await batch.commit();
  invalidateRegistrationsCache();
  return referenceCode;
};

/**
 * Creates PREPAID registration documents and atomically deducts credits.
 * Uses a single Firestore transaction to prevent race conditions.
 * Returns the PREP-YYYY-NNN reference code.
 */
export const createPrepaidRegistrationBatch = async (
  coachId: string,
  coachEmail: string,
  competitionId: string,
  competitionName: string,
  athletes: Array<{
    athleteId: string;
    athleteSnapshot: {
      firstName: string;
      lastName: string;
      dateOfBirth: string;
    };
    club: string;
    weightCategory: string;
    division: "Male" | "Female";
    category: string;
    price: number;
  }>,
  creditPackageId: string,
  coachName?: string
): Promise<string> => {
  // The server re-derives identity, competition metadata, athlete snapshots,
  // and price. Legacy arguments remain only to preserve this public API.
  void coachId;
  void coachEmail;
  void competitionName;
  void coachName;
  const functions = getFunctions(undefined, "europe-central2");
  const callable = httpsCallable<
    {
      competitionId: string;
      creditPackageId: string;
      athletes: Array<{
        athleteId: string;
        club: string;
        weightCategory: string;
        division: "Male" | "Female";
        category: string;
      }>;
    },
    { success: boolean; referenceCode: string; registrationCount: number }
  >(functions, "createPrepaidRegistration");
  const response = await callable({
    competitionId,
    creditPackageId,
    athletes: athletes.map((athlete) => ({
      athleteId: athlete.athleteId,
      club: athlete.club,
      weightCategory: athlete.weightCategory,
      division: athlete.division,
      category: athlete.category,
    })),
  });

  invalidateRegistrationsCache();
  invalidateCreditPackagesCacheFromRegistration();
  return response.data.referenceCode;
};

/** Metadata captured alongside every registration edit for the audit log. */
export interface RegistrationEditor {
  editorUid: string;
  editorEmail: string;
  /** Display name of the editor; stored in `weightChangedBy` on weight edits. */
  editorName?: string;
  changedBy: "coach" | "admin";
}

/**
 * Updates a registration AND writes a registrationAuditLog entry in the same
 * transaction. The two writes succeed or fail together — no orphan edits, no
 * orphan audit rows.
 *
 * Coach-side callers should additionally enforce the weight-change deadline
 * client-side (the security rules are authoritative). No-op edits (where
 * every requested field already matches) are dropped silently and produce
 * no audit entry.
 */
export const updateRegistration = async (
  registrationId: string,
  updates: Partial<
    Pick<
      Registration,
      | "club"
      | "weightCategory"
      | "division"
      | "category"
      | "price"
      | "athleteId"
      | "athleteSnapshot"
    >
  >,
  editor: RegistrationEditor
): Promise<void> => {
  const ref = doc(db, "registrations", registrationId);

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error("Registration not found");
    const before = snap.data() as Registration;

    // Build the diff. Skip athleteId / athleteSnapshot from the audit body —
    // swapping the underlying athlete is a heavy operation that admins do
    // rarely and shouldn't pollute the change log. Their `from`/`to` would
    // also be objects, not human-readable strings.
    const trackable: Array<keyof Registration> = [
      "club",
      "weightCategory",
      "division",
      "category",
      "price",
    ];
    const changes: Record<string, { from: unknown; to: unknown }> = {};
    for (const key of trackable) {
      if (!(key in updates)) continue;
      const next = (updates as any)[key];
      const prev = (before as any)[key];
      if (next === undefined) continue;
      if (prev === next) continue;
      changes[key as string] = { from: prev ?? null, to: next };
    }

    // No-op (e.g. user clicked Save without changing anything that we track).
    if (Object.keys(changes).length === 0 && !("athleteId" in updates)) return;

    // When the weight class actually moved, stamp who changed it and when.
    // These two fields drive the public "weight updated by …" line and the
    // coach-side once-per-hour rate limit. They're added to the SAME write so
    // a coach's update touches exactly { weightCategory, weightLastChanged,
    // weightChangedBy } — which is the key set the security rule allows.
    const finalUpdate: Record<string, any> = { ...updates };
    if ("weightCategory" in changes) {
      finalUpdate.weightLastChanged = serverTimestamp();
      finalUpdate.weightChangedBy = editor.editorName || editor.editorEmail || "";
      // Coach weight changes are capped at MAX_WEIGHT_CHANGES per athlete.
      // Increment the per-registration counter on COACH edits only — admins
      // are exempt and never consume it. The Firestore rules independently
      // verify the new value is exactly old+1 and that old was below the cap,
      // so this enforcement holds even if the client check is bypassed.
      if (editor.changedBy === "coach") {
        const currentCount = (before as any).weightChangeCount ?? 0;
        if (currentCount >= MAX_WEIGHT_CHANGES) {
          throw new Error("Weight change limit reached for this athlete.");
        }
        finalUpdate.weightChangeCount = currentCount + 1;
      }
    }

    tx.update(ref, finalUpdate);

    // Skip audit log when only athleteId/athleteSnapshot moved — see above.
    if (Object.keys(changes).length === 0) return;

    const auditRef = doc(collection(db, "registrationAuditLog"));
    tx.set(auditRef, {
      registrationId,
      competitionId: before.competitionId,
      athleteId: before.athleteId,
      athleteName: `${before.athleteSnapshot.firstName} ${before.athleteSnapshot.lastName}`.trim(),
      editorUid: editor.editorUid,
      editorEmail: editor.editorEmail,
      changedBy: editor.changedBy,
      changes,
      timestamp: serverTimestamp(),
    });
  });

  invalidateRegistrationsCache();
  invalidateAuditLogCache();
};

/**
 * Deletes a single registration AND writes a "deleted" audit entry in the same
 * transaction — the two succeed or fail together. Used by the admin
 * ViewRegistrations page to remove an athlete from a competition (e.g. a
 * no-show). Idempotent: if the document is already gone the call resolves
 * without writing an audit row.
 *
 * Deletion is admin-only. The Firestore security rules
 * (`allow delete: if isAdmin()`) are authoritative; this helper records who
 * performed the removal for the audit trail and busts the registrations cache
 * so the public list drops the athlete on the next read / manual refresh.
 */
export const deleteRegistration = async (
  registrationId: string,
  editor: RegistrationEditor
): Promise<void> => {
  const ref = doc(db, "registrations", registrationId);

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) return; // already deleted — nothing to do
    const before = snap.data() as Registration;

    tx.delete(ref);

    const auditRef = doc(collection(db, "registrationAuditLog"));
    tx.set(auditRef, {
      registrationId,
      competitionId: before.competitionId,
      athleteId: before.athleteId,
      athleteName: `${before.athleteSnapshot.firstName} ${before.athleteSnapshot.lastName}`.trim(),
      editorUid: editor.editorUid,
      editorEmail: editor.editorEmail,
      changedBy: editor.changedBy,
      action: "deleted",
      // Snapshot the key attributes that were removed so a competition-level
      // audit view can show what the deleted registration looked like.
      changes: {
        division:       { from: before.division ?? null,       to: null },
        weightCategory: { from: before.weightCategory ?? null, to: null },
        category:       { from: before.category ?? null,       to: null },
        club:           { from: before.club ?? null,           to: null },
      },
      timestamp: serverTimestamp(),
    });
  });

  invalidateRegistrationsCache();
  invalidateAuditLogCache();
};

// ---------------------------------------------------------------------------
// READ — flat
// ---------------------------------------------------------------------------

export const getRegistrationsByCompetition = async (
  competitionId: string
): Promise<Registration[]> =>
  withCache(`registrations:comp:${competitionId}`, TTL_REG_SCOPED, async () => {
    const q = query(
      collection(db, "registrations"),
      where("competitionId", "==", competitionId),
      orderBy("registeredAt", "desc")
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Registration));
  });

export const getAllRegistrations = async (): Promise<Registration[]> =>
  withCache("registrations:all", TTL_REG_LIST, async () => {
    const q = query(
      collection(db, "registrations"),
      orderBy("registeredAt", "desc")
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Registration));
  });

/**
 * Legacy unbounded-ish fetcher. Now capped at 50 newest docs to bound
 * read cost on growing data. New callers should prefer
 * getRecentFreeRegistrations(...) below for explicit pagination.
 */
export const getAllFreeRegistrations = async (): Promise<Registration[]> =>
  withCache("registrations:all:free", TTL_REG_LIST, async () => {
    const q = query(
      collection(db, "registrations"),
      where("isFree", "==", true),
      orderBy("registeredAt", "desc"),
      limit(50)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Registration));
  });

/**
 * Fetches all prepaid registrations across all coaches.
 * Used in AdminRecentRegistrations to display prepaid groups.
 *
 * NOTE: This is the legacy unbounded fetcher kept for backward compatibility.
 * New callers should prefer getRecentPrepaidRegistrations(...) below, which
 * is server-side paginated.
 */
export const getAllPrepaidRegistrations = async (): Promise<Registration[]> =>
  withCache("registrations:all:prepaid", TTL_REG_LIST, async () => {
    const q = query(
      collection(db, "registrations"),
      where("paymentType", "==", "prepaid"),
      orderBy("registeredAt", "desc"),
      limit(50)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Registration));
  });

// ---------------------------------------------------------------------------
// READ — server-side paginated (Phase 17+ scalability)
// ---------------------------------------------------------------------------

/**
 * Generic paginated page shape. `cursor` is the Firestore document snapshot
 * needed to call the next page; pass it back as `startAfterDoc` to continue.
 * `hasMore` is true when the page is exactly `pageSize` long (so callers can
 * show / hide a "Load more" button without an extra query).
 */
export interface RegistrationPage {
  rows: Registration[];
  cursor: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

/** Recent admin_manual (bank transfer) registrations across all coaches. */
export const getRecentFreeRegistrations = async (
  pageSize = 25,
  startAfterDoc: QueryDocumentSnapshot<DocumentData> | null = null
): Promise<RegistrationPage> => {
  const constraints: any[] = [
    where("isFree", "==", true),
    orderBy("registeredAt", "desc"),
  ];
  if (startAfterDoc) constraints.push(startAfter(startAfterDoc));
  constraints.push(limit(pageSize));
  const q = query(collection(db, "registrations"), ...constraints);
  const snap = await getDocs(q);
  const docs = snap.docs;
  return {
    rows: docs.map((d) => ({ id: d.id, ...d.data() } as Registration)),
    cursor: docs.length > 0 ? docs[docs.length - 1] : null,
    hasMore: docs.length === pageSize,
  };
};

/** Recent prepaid (credit package) registrations across all coaches. */
export const getRecentPrepaidRegistrations = async (
  pageSize = 25,
  startAfterDoc: QueryDocumentSnapshot<DocumentData> | null = null
): Promise<RegistrationPage> => {
  const constraints: any[] = [
    where("paymentType", "==", "prepaid"),
    orderBy("registeredAt", "desc"),
  ];
  if (startAfterDoc) constraints.push(startAfter(startAfterDoc));
  constraints.push(limit(pageSize));
  const q = query(collection(db, "registrations"), ...constraints);
  const snap = await getDocs(q);
  const docs = snap.docs;
  return {
    rows: docs.map((d) => ({ id: d.id, ...d.data() } as Registration)),
    cursor: docs.length > 0 ? docs[docs.length - 1] : null,
    hasMore: docs.length === pageSize,
  };
};

/**
 * Fetches all registrations whose registeredAt falls in [startDate, endDate).
 * Cached per (startMs, endMs) tuple for TTL_REG_LIST.
 *
 * Used by AdminRegistrations to scope the heavy grouped view to a single year
 * (or other window) instead of pulling the entire collection.
 */
export const getRegistrationsInRange = async (
  startDate: Date,
  endDate: Date
): Promise<Registration[]> => {
  const startMs = startDate.getTime();
  const endMs   = endDate.getTime();
  return withCache(
    `registrations:range:${startMs}:${endMs}`,
    TTL_REG_LIST,
    async () => {
      const q = query(
        collection(db, "registrations"),
        where("registeredAt", ">=", Timestamp.fromDate(startDate)),
        where("registeredAt", "<",  Timestamp.fromDate(endDate)),
        orderBy("registeredAt", "desc")
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Registration));
    }
  );
};

// ---------------------------------------------------------------------------
// READ — aggregate document (maintained by Cloud Function)
// ---------------------------------------------------------------------------

/** Schema of aggregates/registrationStats (maintainRegistrationStats CF). */
export interface RegistrationStats {
  total: number;
  byType: { online: number; prepaid: number; admin_manual: number };
  byYear: Record<string, { total: number; online: number; prepaid: number; admin_manual: number }>;
  byCompetition: Record<string, number>;
}

/**
 * Reads the pre-computed aggregate doc. Returns null if it doesn't exist yet
 * (e.g. on a fresh deploy before the trigger has fired). Callers should fall
 * back gracefully — the aggregate is an optimization, not a source of truth.
 */
export const getRegistrationStats = async (): Promise<RegistrationStats | null> =>
  withCache(
    "registrations:aggregate",
    TTL_REG_LIST,
    async () => {
      const snap = await getDoc(doc(db, "aggregates", "registrationStats"));
      if (!snap.exists()) return null;
      const data = snap.data() as Partial<RegistrationStats>;
      return {
        total: data.total ?? 0,
        byType: {
          online:       data.byType?.online       ?? 0,
          prepaid:      data.byType?.prepaid      ?? 0,
          admin_manual: data.byType?.admin_manual ?? 0,
        },
        byYear: data.byYear ?? {},
        byCompetition: data.byCompetition ?? {},
      };
    },
    { persist: true }
  );

export const getRegistrationsByCoach = async (
  coachId: string
): Promise<Registration[]> =>
  withCache(`registrations:coach:${coachId}`, TTL_REG_SCOPED, async () => {
    const q = query(
      collection(db, "registrations"),
      where("coachId", "==", coachId),
      orderBy("registeredAt", "desc")
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Registration));
  });

export const getFreeRegistrationsByCoach = async (
  coachId: string
): Promise<Registration[]> =>
  withCache(`registrations:coach:${coachId}:free`, TTL_REG_SCOPED, async () => {
    const q = query(
      collection(db, "registrations"),
      where("coachId", "==", coachId),
      where("isFree", "==", true),
      orderBy("registeredAt", "desc")
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Registration));
  });

/**
 * Fetches all prepaid registrations for a coach (paymentType === "prepaid").
 * Used in CoachHistory to display prepaid registration groups.
 */
export const getPrepaidRegistrationsByCoach = async (
  coachId: string
): Promise<Registration[]> =>
  withCache(`registrations:coach:${coachId}:prepaid`, TTL_REG_SCOPED, async () => {
    const q = query(
      collection(db, "registrations"),
      where("coachId", "==", coachId),
      where("paymentType", "==", "prepaid"),
      orderBy("registeredAt", "desc")
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Registration));
  });

/**
 * Fetches all registrations that consumed a specific credit package.
 * Used in ManageCreditPackages to show usage details.
 */
export const getRegistrationsByCreditPackage = async (
  creditPackageId: string
): Promise<Registration[]> =>
  withCache(`registrations:creditPackage:${creditPackageId}`, TTL_REG_SCOPED, async () => {
    const q = query(
      collection(db, "registrations"),
      where("creditPackageId", "==", creditPackageId),
      orderBy("registeredAt", "desc")
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Registration));
  });

export const isAthleteRegisteredByCoach = async (
  athleteId: string,
  competitionId: string,
  coachId: string
): Promise<boolean> => {
  const q = query(
    collection(db, "registrations"),
    where("competitionId", "==", competitionId),
    where("coachId", "==", coachId),
    where("athleteId", "==", athleteId)
  );
  const snapshot = await getDocs(q);
  return !snapshot.empty;
};

export const isAthleteRegisteredForCompetition = async (
  athleteId: string,
  competitionId: string
): Promise<boolean> => {
  const q = query(
    collection(db, "registrations"),
    where("competitionId", "==", competitionId),
    where("athleteId", "==", athleteId)
  );
  const snapshot = await getDocs(q);
  return !snapshot.empty;
};

// ---------------------------------------------------------------------------
// READ — grouped
// ---------------------------------------------------------------------------

/**
 * Pure transformer: groups a registration list by competition, then by club.
 * Extracted so AdminRegistrations can pass a date-range-scoped list instead
 * of always reading every registration.
 */
export const groupRegistrationsByCompetition = (
  regs: Registration[]
): CompetitionStats[] => {
  const compMap = new Map<string, { name: string; regs: Registration[] }>();
  for (const r of regs) {
    if (!compMap.has(r.competitionId)) {
      compMap.set(r.competitionId, { name: r.competitionName, regs: [] });
    }
    compMap.get(r.competitionId)!.regs.push(r);
  }

  return Array.from(compMap.entries()).map(
    ([competitionId, { name, regs }]) => {
      const clubMap = new Map<string, Registration[]>();
      for (const r of regs) {
        const arr = clubMap.get(r.club) ?? [];
        arr.push(r);
        clubMap.set(r.club, arr);
      }

      const clubs: ClubStats[] = Array.from(clubMap.entries())
        .map(([clubName, athletes]) => ({
          clubName,
          athletes,
          totalAmount: athletes.reduce((s, a) => s + a.price, 0),
        }))
        .sort((a, b) => b.athletes.length - a.athletes.length);

      return {
        competitionId,
        competitionName: name,
        clubs,
        totalAthletes: regs.length,
        totalClubs: clubMap.size,
        totalRevenue: regs.reduce((s, r) => s + r.price, 0),
      };
    }
  );
};

/**
 * Back-compat wrapper. Old callers fetched + grouped in one go; the heavy
 * "fetch everything" path is preserved here, but new callers should pair
 * getRegistrationsInRange(...) with groupRegistrationsByCompetition(...)
 * for a much smaller working set.
 */
export const getGroupedRegistrationStats = async (): Promise<
  CompetitionStats[]
> => {
  const all = await getAllRegistrations();
  return groupRegistrationsByCompetition(all);
};

// ---------------------------------------------------------------------------
// ADMIN helpers
// ---------------------------------------------------------------------------

// Note: the previous getRegistrationCountForCompetition helper was removed.
// Counts now live on competitions/{compId}.registrationCount, maintained by
// the maintainCompetitionCount Cloud Function — no extra Firestore query
// needed on the admin pages.

export const deleteRegistrationsByCompetition = async (
  competitionId: string
): Promise<void> => {
  const q = query(
    collection(db, "registrations"),
    where("competitionId", "==", competitionId)
  );
  const snapshot = await getDocs(q);
  const batch = writeBatch(db);
  snapshot.docs.forEach((d) => {
    batch.delete(d.ref);
  });
  await batch.commit();
  invalidateRegistrationsCache();
};
